
/**
 * Generate a browser fingerprint based on various browser characteristics
 * This creates a reasonably unique identifier for the device/browser combination
 */
export const generateBrowserFingerprint = (): string => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  let canvasFingerprint = '';
  
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillText('Browser fingerprint', 2, 2);
    canvasFingerprint = canvas.toDataURL();
  }

  const fingerprint = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages?.join(',') || '',
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${screen.width}x${screen.height}`,
    screenColorDepth: screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
    canvas: canvasFingerprint.slice(-50), // Last 50 chars of canvas fingerprint
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    webglVendor: getWebGLVendor(),
    webglRenderer: getWebGLRenderer(),
  };

  // Create a hash from the fingerprint data
  const fingerprintString = JSON.stringify(fingerprint);
  return generateHashFromString(fingerprintString);
};

/**
 * Get WebGL vendor information
 */
const getWebGLVendor = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || '';
      }
    }
  } catch (e) {
    // Ignore errors
  }
  return '';
};

/**
 * Get WebGL renderer information
 */
const getWebGLRenderer = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        return gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || '';
      }
    }
  } catch (e) {
    // Ignore errors
  }
  return '';
};

/**
 * Generate a simple hash from a string
 */
const generateHashFromString = (str: string): string => {
  let hash = 0;
  if (str.length === 0) return hash.toString();
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Convert to positive hex string
  return Math.abs(hash).toString(16);
};

/**
 * Get or create a persistent device ID
 * This will try to get an existing device ID from localStorage,
 * or generate a new one if it doesn't exist
 */
export const getDeviceId = (): string => {
  const existingDeviceId = localStorage.getItem('device_id');
  
  if (existingDeviceId) {
    return existingDeviceId;
  }
  
  const newDeviceId = generateBrowserFingerprint();
  localStorage.setItem('device_id', newDeviceId);
  
  console.log('Generated new device ID:', newDeviceId);
  return newDeviceId;
};
